import { Component, OnInit, inject } from '@angular/core';
import { ApiService } from '../../services/api.service';
import { CommonModule } from '@angular/common';
import { CompanyStatus, LastExecutionHistory } from '../../models/api.model';
import { FilterService } from '../../services/filter.service';
import { Router } from '@angular/router';
import { AuthService } from '../../auth/auth.service';

type ScheduleType = 'Daily' | 'Hourly' | 'High-Frequency' | 'NOT FOUND';
type ExecutionStatus = 'OK' | 'FAILED' | 'NOT FOUND';

@Component({
  selector: 'app-total-scheduled-card',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './total-scheduled-card.component.html',
  styleUrls: ['./total-scheduled-card.component.css'],
})
export class TotalScheduledCardComponent implements OnInit {
  totalReports: number | null = null;
  companyDetails: CompanyStatus[] | null = null;
  filteredCompanyDetails: CompanyStatus[] | null = null;
  selectedCompanyId: number | null = null;
  sessionRuc: string | null = inject(AuthService).getSession()?.ruc ?? null;

  typeCounts: Record<ScheduleType, number> = { Daily: 0, Hourly: 0, 'High-Frequency': 0, 'NOT FOUND': 0 };
  statusCounts: Record<ExecutionStatus, number> = { OK: 0, FAILED: 0, 'NOT FOUND': 0 };
  readonly typeOrder: ScheduleType[] = ['Daily', 'Hourly', 'High-Frequency', 'NOT FOUND'];
  readonly statusOrder: ExecutionStatus[] = ['OK', 'FAILED', 'NOT FOUND'];

  constructor(
    private apiService: ApiService,
    private filterService: FilterService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.apiService.getReportStatus().subscribe((response) => {
      this.companyDetails = response.list;
      this.updateTotalReports();
      this.filterService.selectedCompanyId$.subscribe((id) => {
        this.selectedCompanyId = id;
        this.filterCompanies();
        this.updateTotalReports();
        if (id !== null) {
          this.loadStatusBreakdown(id);
        } else {
          this.resetCounts();
        }
      });
    });
  }

  filterCompanies(): void {
    if (this.selectedCompanyId !== null && this.companyDetails) {
      this.filteredCompanyDetails = this.companyDetails.filter(
        (company) => company.id_cia === this.selectedCompanyId
      );
    } else {
      this.filteredCompanyDetails = this.companyDetails;
    }
  }

  updateTotalReports(): void {
    const detailsToSum = this.filteredCompanyDetails || [];
    this.totalReports = detailsToSum.reduce(
      (sum, company) => sum + company.num_reports,
      0
    );
  }

  onCompanySelected(company: CompanyStatus | null): void {
    const id = company ? company.id_cia : -1;
    const encodedId = btoa(id.toString());
    if (id === -1) {
      this.router.navigate(['/']);
    } else {
      this.router.navigate(['/dashboard', encodedId]);
    }
  }

  private loadStatusBreakdown(id_cia: number): void {
    this.apiService.getLastExecutionDetails(id_cia).subscribe((res) => {
      this.computeBreakdowns(res.list || []);
    });
  }

  private resetCounts(): void {
    this.typeCounts = { Daily: 0, Hourly: 0, 'High-Frequency': 0, 'NOT FOUND': 0 };
    this.statusCounts = { OK: 0, FAILED: 0, 'NOT FOUND': 0 };
  }

  private computeBreakdowns(history: LastExecutionHistory[]): void {
    this.resetCounts();
    for (const h of history) {
      const t = (h.schedule_type || 'NOT FOUND') as ScheduleType;
      const tKey = (this.typeOrder.includes(t) ? t : 'NOT FOUND') as ScheduleType;
      this.typeCounts[tKey]++;

      const s = (h.last_execution_status || 'NOT FOUND') as ExecutionStatus;
      const sKey = (this.statusOrder.includes(s) ? s : 'NOT FOUND') as ExecutionStatus;
      this.statusCounts[sKey]++;
    }
  }

  typeBadgeClass(type: ScheduleType): string {
    switch (type) {
      case 'Daily':           return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
      case 'Hourly':          return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300';
      case 'High-Frequency':  return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300';
      default:                return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  }

  statusBadgeClass(status: ExecutionStatus): string {
    switch (status) {
      case 'OK':       return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
      case 'FAILED':   return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300';
      default:         return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  }
}
